/**
 * Plain-language help text for the Market Insight (VMA) panel, shared by the Broker
 * and Client VIN pop-outs so both views describe a field identically.
 *
 * Keyed by the vehicle field name, not the display label.
 */
export const VMA_FIELD_HELP: Record<string, string> = {
  wholesale_supply_level:
    'How much wholesale inventory exists for this year/make/model right now: Low, Medium, High or Excessive. High or Excessive means you are competing against a lot of similar units.',

  wholesale_demand_level:
    'How actively wholesale buyers are pursuing this year/make/model: Cold, Warm or Hot. Hot means buyers are engaging strongly.',

  wholesale_avg_days_to_sell:
    'Average days a single unit takes to sell at wholesale once it is listed. This is speed per unit — lower is faster.',

  wholesale_days_supply:
    'How many days the current wholesale inventory would last at the trailing 90-day sales pace (inventory divided by daily sales). This measures market saturation, not the speed of any one unit — high days supply means the market is oversupplied even if individual cars sell quickly.',

  buyer_coverage:
    'How many distinct buyers have been active in this vehicle\u2019s cohort (make, model and year band). A small number means a thin market with few likely bidders. Blank means we have no signal for this cohort. This comes from our own buyer activity, not from VMA.',

  wholesale_sales_efficiency:
    'Percentage of listed wholesale units that actually sold. Lower means more units are running through without finding a buyer.',

  wholesale_sales_effectiveness:
    'Percentage of expected value that selling units actually realized. Lower means units are selling below what the market would suggest.',

  retail_supply_level:
    'How much retail inventory exists for this year/make/model: Low, Medium, High or Excessive. This is the bucketed form of retail days supply.',

  retail_demand_level:
    'How actively retail shoppers are pursuing this year/make/model: Cold, Warm or Hot.',

  retail_avg_days_to_sell:
    'Average days a single unit takes to sell on a retail lot once listed. Speed per unit — lower is faster.',

  // The DMA/national denominator split is a real VMA quirk, not a bug on our side.
  retail_days_supply:
    'How many days current retail inventory would last at the trailing 90-day pace. Important caveat: VMA calculates this differently depending on the market. For a specific local market (DMA) it is measured against units LISTED; when only a national figure is available it is measured against units SOLD. A local value and a national value therefore answer slightly different questions and should not be compared directly against each other.',

  retail_rmi:
    'Retail Market Index — the typical retail asking price for this year/make/model in this market.',

  retail_spread:
    'Retail Market Index minus MMR: the gap between what the vehicle retails for and what it is worth at wholesale. A wider spread means more margin available to a retail buyer, which usually supports stronger wholesale bidding.',

  retail_retention:
    'Retail retention ratio — how well this year/make/model holds its value at retail. Higher retains value better.',

  retail_avg_list_price:
    'Average retail list price for this year/make/model in this market.',
};

/**
 * Help text for the price ladder. These sit alongside the VMA figures and are
 * easily confused with them — three separate numbers are all called some form
 * of "retail" — so each one states its subject (this VIN vs the wider market)
 * explicitly.
 */
export const PRICE_FIELD_HELP: Record<string, string> = {
  floor_price:
    'The seller\u2019s minimum acceptable price for this vehicle. Bids below it do not sell the car.',

  buy_now_price:
    'The price a buyer can pay right now to take this vehicle without bidding.',

  base_mmr:
    'Manheim Market Report value for the year/make/model in average condition and mileage \u2014 before any adjustment for this specific car.',

  adjusted_mmr:
    'MMR adjusted for THIS vehicle\u2019s condition grade, mileage and region. This is the wholesale number to price against.',

  mmr_range:
    'The below/above spread around adjusted MMR \u2014 the range wholesale transactions for a car like this typically land in.',

  cohort_avg_paid:
    'What buyers in this vehicle\u2019s cohort (make, model and year band) have actually paid recently. Unlike MMR this is observed transactions from our own data, not a published valuation.',

  retail_value:
    'Estimated retail value of THIS specific vehicle \u2014 what it should be worth on a retail lot given its condition and mileage.',

  retail_value_range:
    'The below/above spread around this vehicle\u2019s estimated retail value.',

  retail_list_price:
    'The retail asking price observed for THIS specific vehicle, where one is available.',

  retail_rmi_ladder:
    'Retail Market Index \u2014 the typical retail ASKING price for the year/make/model across this market. It describes the market, not this VIN, so it will differ from this vehicle\u2019s own retail value.',

  retail_avg_list_ladder:
    'The average retail list price across all listings of this year/make/model in this market. Similar to RMI but a plain average of what is currently listed rather than an index.',
};

/** Footnote shown under the VMA grid on both views. */
export const VMA_PANEL_NOTE =
  'All figures describe the market for this year\u00b7make\u00b7model, not this specific VIN \u2014 using the vehicle\u2019s local market (DMA) where available, otherwise national. Days-to-sell is how long one unit takes to sell; days supply is how long existing inventory lasts at the current pace.';

/** Broker-only: the urgency score does not exist on the Client View. */
export const VMA_PANEL_NOTE_BROKER =
  `${VMA_PANEL_NOTE} Wholesale signals also feed the urgency score; retail is context only.`;
